import { yupResolver } from '@hookform/resolvers/yup';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    Grid,
    MenuItem
} from '@mui/material';
import { addDays, isBefore, startOfDay } from 'date-fns';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import * as yup from 'yup';
import { FormCheckbox, FormDatePicker, FormSelect } from '../../../common/form';
import { ProgressButton } from '../../../common/ProgressButton';
import { RootState } from '../../../store';
import CustomerTree from '../../customer/CustomerTree';
import { getCurrentUser } from '../../user/redux/userSelectors';
import { getAppSettings, getPricelistMailSettings, getSettingsUi } from '../redux/settingsSelectors';
import { sendPricelistsToCustomers } from '../redux/settingsSlices';

const mapStateToProps = (state: RootState) => ({
    isSending: getSettingsUi(state).isSendingPricelistMails,
    currentUserEmail: getCurrentUser(state)?.username,
    pricelistMailSettings: getPricelistMailSettings(state),
    mailSendouts: getAppSettings(state).mailSendouts
});

type OwnProps = {
    closeDialog(): void;
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

type SendPricelistMailsForm = {
    mailSendoutId: string;
    customerIds?: number[];
    validAt?: Date;
    sendToAllCustomers: boolean;
};

const schema: yup.SchemaOf<SendPricelistMailsForm> = yup.object().shape({
    mailSendoutId: yup.string().required().label('Mail sendout'),
    customerIds: yup
        .array()
        .when('sendToAllCustomers', {
            is: false,
            then: yup.array().min(1, 'At least one customer must be selected').of(yup.number().required()),
            otherwise: yup.array()
        })
        .label('Customers'),
    validAt: yup
        .date()
        .required()
        .min(startOfDay(addDays(new Date(), 1)), "'Valid at' must be a future date")
        .label('Valid at'),
    sendToAllCustomers: yup.bool().required()
});

const SendPricelistsToCustomersDialog = ({
    isSending,
    closeDialog,
    pricelistMailSettings,
    mailSendouts
}: Props) => {
    const dispatch = useDispatch();

    const minDate = useMemo(() => startOfDay(addDays(new Date(), 1)), []);
    const { control, handleSubmit, watch } = useForm<SendPricelistMailsForm>({
        defaultValues: useMemo(
            () => ({
                customerIds: [],
                validAt: minDate,
                sendToAllCustomers: false
            }),
            [minDate]
        ),
        resolver: yupResolver(schema)
    });

    const handleFormSubmit = (form: SendPricelistMailsForm) => {
        dispatch(
            sendPricelistsToCustomers.request({
                mailSendoutId: form.mailSendoutId,
                payload: {
                    customerIds: form.customerIds ?? [],
                    validAt: form.validAt!.toISOString(),
                    sendToAllCustomers: form.sendToAllCustomers
                }
            })
        );
    };

    const watchSendToAllCustomers: boolean = watch('sendToAllCustomers');

    const shouldDisableDate = (date: Date | null) => {
        if (date == null) {
            return false;
        }

        return isBefore(date, minDate);
    };

    return (
        <Dialog open={true}>
            <DialogTitle>Send pricelist mails to customers</DialogTitle>
            <DialogContent sx={{ height: 600, width: 500 }}>
                <form id="sendPricelistMailsToUserDialog" onSubmit={handleSubmit(handleFormSubmit)}>
                    <Grid container direction="column" spacing={4}>
                        <Grid item xs={6}>
                            <FormSelect label="Mail sendout" name="mailSendoutId" control={control}>
                                {mailSendouts.map(s => (
                                    <MenuItem key={s.mailSendoutId} value={s.mailSendoutId}>
                                        {s.name}
                                    </MenuItem>
                                ))}
                            </FormSelect>
                        </Grid>
                        <Grid item xs={6}>
                            <FormDatePicker
                                name="validAt"
                                label="Valid at"
                                control={control}
                                inputFormat="yyyy-MM-dd"
                                disablePast={true}
                                shouldDisableDate={shouldDisableDate}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormCheckbox
                                label="Send to all customers"
                                name="sendToAllCustomers"
                                control={control}
                            />
                        </Grid>
                        <Grid item>
                            <Controller
                                control={control}
                                name="customerIds"
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <Box
                                        component="div"
                                        sx={
                                            watchSendToAllCustomers
                                                ? { pointerEvents: 'none', opacity: 0.5 }
                                                : undefined
                                        }
                                    >
                                        <CustomerTree
                                            multiSelect
                                            selectedCustomerIds={value}
                                            setSelectedCustomerIds={onChange}
                                            filter={c =>
                                                c.customerEmails.length > 0 &&
                                                c.pricelistMailAttachmentTypes.length > 0 &&
                                                pricelistMailSettings.customerIds.includes(c.customerId)
                                            }
                                        />
                                        {error != null && (
                                            <FormHelperText error={true}>{error.message}</FormHelperText>
                                        )}
                                    </Box>
                                )}
                            />
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions>
                <Button onClick={closeDialog}>Cancel</Button>
                <ProgressButton
                    color="primary"
                    type="submit"
                    form="sendPricelistMailsToUserDialog"
                    isLoading={isSending}
                >
                    Send
                </ProgressButton>
            </DialogActions>
        </Dialog>
    );
};

export default connect(mapStateToProps)(SendPricelistsToCustomersDialog);
